/**
 * callOutcomeLabels -- the ONLY source of a call outcome / call status label.
 *
 * Why this file exists (#82 / #98 / signature defect):
 * the product carried several competing, diverging status dictionaries
 * (ProspectDetailPanel, CRMActivityFeed, ContactsScreen, LiveFeedPanel,
 * InlineCallStatus, CallsSection, ClientCalls, CRMScreen, ClientPortalView,
 * PostCallScore, the vf/xdial design-system badges) and the history badge
 * fell back to the raw value when a key was missing, so `voicemail`,
 * `ios_filter`, `canceled` showed up as-is between two translated labels.
 *
 * Rule (ADR-032, section 6ter rule 1): never add another dictionary, plug into
 * the one that is authoritative. LABELS come from the i18n catalog
 * `dialer:status.*`; this module only maps value -> i18n key (+ a default
 * tone) and guarantees that no raw value ever reaches the screen.
 *
 * Since 2026-09-23 every local map keeps ONLY its view-specific visuals
 * (colours, icons, animation flags); the label is always `outcomeLabel()`.
 * `callOutcomeLabels.test.ts` checks that every key below exists in both
 * language catalogs.
 */

/** outcome / status value -> key of the `dialer:status.*` catalog section */
export const OUTCOME_I18N_KEY: Record<string, string> = {
  // prospect-level states
  new:                'new',
  pending:            'new',
  recall:             'recall',
  qualified:          'qualified',
  do_not_call:        'doNotCall',
  lost:               'lost',
  // call outcomes
  contacted:          'contacted',
  connected:          'contacted',
  human_detected:     'humanDetected',
  interested:         'interested',
  meeting_booked:     'booked',
  meeting_scheduled:  'booked',
  booked:             'booked',
  callback:           'callback',
  callback_requested: 'callback',
  callback_scheduled: 'callback',
  not_interested:     'notInterested',
  no_answer:          'noAnswer',
  voicemail:          'voicemail',
  left_voicemail:     'leftVoicemail',
  not_reachable:      'unreachable',
  interrupted:        'interrupted',
  wrong_number:       'wrongNumber',
  gatekeeper:         'gatekeeper',
  sms_sent:           'smsSent',
  // technical statuses reported by telephony
  dialing:            'dialing',
  ringing:            'ringing',
  ai_analyzing:       'aiAnalyzing',
  verifying:          'verifying',
  connected_to_agent: 'live',
  online:             'live',
  live:               'live',
  hold:               'hold',
  voicemail_playing:  'voicemailPlaying',
  completed:          'completed',
  ended:              'completed',
  ios_filter:         'iosFilter',
  no_number:          'noNumber',
  canceled:           'canceled',
  cancelled:          'canceled',
  busy:               'busy',
  failed:             'failed',
  disabled:           'disabled',
  other:              'other',
};

/** Terminal style fallback, used even when the fallback key is missing. */
export const TON_NEUTRE = 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200';

/** badge tone, aligned on the fixed colour code requested in #80 */
export const OUTCOME_TONE: Record<string, string> = {
  new:          'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200',
  recall:       'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-200',
  qualified:    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  doNotCall:    'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  lost:         'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  booked:       'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  contacted:    'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  humanDetected:'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  callback:     'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  interested:   'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  notInterested:'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  noAnswer:     'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  voicemail:    'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  leftVoicemail:'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  unreachable:  'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  wrongNumber:  'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  gatekeeper:   'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200',
  smsSent:      'bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200',
  dialing:      'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  ringing:      'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200',
  aiAnalyzing:  'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
  verifying:    'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
  live:         'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  hold:         'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  voicemailPlaying: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
  completed:    'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  iosFilter:    'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200',
  noNumber:     'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  canceled:     'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  interrupted:  'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
  busy:         'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  failed:       'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  disabled:     'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
  other:        'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200',
};

/** Terminal fallback: never a raw value, never an empty string. */
export const OUTCOME_FALLBACK_KEY = 'other';

export type TranslateFn = (k: string) => string;

/** i18n key (without the `dialer:status.` prefix) for a raw value. */
export function outcomeI18nKey(value: string | null | undefined): string {
  const brut = (value || '').toString();
  return OUTCOME_I18N_KEY[brut] || OUTCOME_FALLBACK_KEY;
}

/** True when the raw value is known to the catalog (no fallback needed). */
export function isKnownOutcome(value: string | null | undefined): boolean {
  return Boolean(value && OUTCOME_I18N_KEY[value]);
}

/**
 * outcomeLabel -- the translated label of a raw outcome / status value.
 *
 * @param value raw value (may be null or unknown: the fallback label is used)
 * @param t     the translation function (namespace `dialer` reachable)
 */
export function outcomeLabel(value: string | null | undefined, t: TranslateFn): string {
  return t('dialer:status.' + outcomeI18nKey(value));
}

/**
 * resolveOutcomeMeta -- label + tone of a call outcome.
 *
 * @param outcome    outcome value (may be null or unknown)
 * @param callStatus technical status used as fallback when the outcome is absent
 * @param t          the translation function (namespace `dialer` reachable)
 */
export function resolveOutcomeMeta(
  outcome: string | null | undefined,
  callStatus: string | null | undefined,
  t: TranslateFn,
): { label: string; tone: string; key: string } {
  const key = outcomeI18nKey(outcome || callStatus);
  // noUncheckedIndexedAccess: even the fallback is `string | undefined`, so we
  // end on a literal to make sure `tone` is always a string.
  return {
    key,
    label: t('dialer:status.' + key),
    tone: OUTCOME_TONE[key] || OUTCOME_TONE[OUTCOME_FALLBACK_KEY] || TON_NEUTRE,
  };
}

/**
 * The outcomes a caller can pick by hand in an activity box (#82/#98).
 * Same source as the display: one vocabulary to read and to write.
 */
export const DISPOSITION_VALUES = [
  // `contacted` is in the list: it is the most common outcome, and without it
  // the box of a connected call showed no pre-filled outcome.
  'contacted',
  'callback_requested',
  'interested',
  'not_interested',
  'voicemail',
  'no_answer',
] as const;
